import { Text } from "@/components/podkit/typography/Text";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import type { FC } from "react";
import type { PlainWebhook } from "@/queries/webhook-queries";
import { Link } from "react-router-dom";
import { webhookDetailsUrl } from "@/utils/automations";
import { pluralize } from "@/utils/strings";
import {
    isWebhookFullyConfigured,
    WEBHOOK_PROVIDER_CONFIG,
    WEBHOOK_TYPE_CONFIG,
} from "@/components/webhooks/webhook-config";
import { timeAgo } from "@/format/time";
import { Timestamp } from "@bufbuild/protobuf";
import { Pill } from "@/components/podkit/status/Pill";

type WebhookListItemProps = PropsWithClassName<{
    webhook: PlainWebhook;
}>;

export const WebhookListItem: FC<WebhookListItemProps> = ({ webhook, className }) => {
    const name = webhook.metadata?.name || "Unnamed webhook";
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const type = webhook.spec?.type ?? WebhookType.UNSPECIFIED;
    const typeName = WEBHOOK_TYPE_CONFIG[type].name;
    const boundWorkflowCount = webhook.boundWorkflowCount;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;
    const configured = isWebhookFullyConfigured(webhook);

    const lastTriggeredText = webhook.lastTriggeredAt
        ? `Last triggered ${timeAgo(new Timestamp(webhook.lastTriggeredAt).toDate())}`
        : "Never triggered";

    return (
        <li
            className={cn(
                "flex items-start gap-3",
                "hover:bg-surface-hover",
                "has-[>a:focus-visible]:ring-1 has-[>a:focus-visible]:ring-inset has-[>a:focus-visible]:ring-content-primary",
                className,
            )}
            data-testid="webhook-list-item"
        >
            <Link
                to={webhookDetailsUrl(webhook.id)}
                state={{ canGoBack: true }}
                className="flex min-w-0 flex-1 items-start gap-3 p-3 focus-visible:ring-0"
                data-tracking-id="webhook-list-item"
            >
                <ProviderIcon size="base" className="shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                        <Text className="truncate text-base font-medium">{name}</Text>
                        {!configured && (
                            <Pill variant="warning" size="sm">
                                Setup incomplete
                            </Pill>
                        )}
                    </div>
                    <Text className="text-sm text-content-secondary">
                        {typeName} • {boundWorkflowCount} {pluralize(boundWorkflowCount, "automation")} •{" "}
                        {lastTriggeredText}
                    </Text>
                </div>
            </Link>
        </li>
    );
};
